import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CANONICAL_BOOKS,
  ensureBookAvailable,
  ensureBibleHydrated,
} from '@/lib/bible/active';
import { chapterVerseTexts, formatVerseRanges } from '@/lib/scripture-anchor';
import { SCRIPTURE_JOURNAL_ENABLED } from '@/lib/feature-flags';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { HL_DOT_HEX, coerceFamily } from '@/lib/highlight-utils';
import { formatRelativeTime } from '@/lib/prayer-utils';
import {
  chapterMarks,
  JOURNAL_PAGE_SIZE,
  PASSAGE_VERSE_NOTE_PREVIEW,
} from '@/lib/journal-aggregate';
import type { VerseMark } from '@/lib/journal-aggregate';
import { useJournalSources } from '@/hooks/useJournalSources';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import JournalPagination from '@/components/JournalPagination/JournalPagination';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import { useSettingsStore } from '@/lib/settings-store';
import { isCoreReadOnly } from '@/lib/core-cutover';
import type { BibleBookmark } from '@/types/bookmark';
import type { Prayer } from '@/types/prayer';
import styles from './JournalPassagePage.module.css';

/**
 * Scripture Journal Passage page at /bible/bookmarks/book/:bookSlug/:chapter
 * (canon-spatial redesign Phase 1, §6 P1), the "open my Bible to here" heart.
 * Renders the chapter's marks ON the verse text: a whole-chapter section, then
 * each marked verse (number + BSB text + highlight chip + a BOUNDED notes/
 * prayers preview, read-only), plus "Read the full chapter" into the reader.
 *
 * Marks are pure structure (synchronous, no BSB). The BSB verse TEXT is an
 * async enhancement resolved once via ensureBibleHydrated -> getChapter ->
 * chapterVerseTexts; on offline/failure the marks still render (notes/prayers
 * carry their own content), and highlight-only verses degrade to reference-only
 * (the reader's offline pattern, not ScriptureReflectionPage's Navigate-away).
 */

// Strict positive integer (rule #71); excludes 0.
const CHAPTER_PATTERN = /^[1-9]\d*$/;

// Local re-implementation of the reader's offline classification (rule #4
// cross-boundary sub-rule; the reader's copy is module-private to
// BibleChapter). "Offline" is claimed ONLY when the device is definitely
// offline — a failed request while apparently online gets the generic retry
// copy instead of "connect once" (2026-08-25 honesty fix, in lockstep with
// BibleChapter + BibleBookIndex). A `true` navigator.onLine is still not
// trusted; the generic copy says "check your connection" to cover it.
function isOfflineHydrationFailure(err: unknown): boolean {
  void err;
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

type LoadState =
  | { kind: 'pending' }
  | { kind: 'ready'; verseTexts: Map<number, string> }
  | { kind: 'failed'; offline: boolean };

type ChapterItem =
  | { kind: 'note'; when: number; bm: BibleBookmark }
  | { kind: 'prayer'; when: number; prayer: Prayer };

// The verse flow interleaves per-verse marks with passage notes (a note whose
// anchor covers 2+ verses renders ONCE, at its first covered verse).
type FlowItem =
  | { kind: 'verse'; at: number; vm: VerseMark }
  | { kind: 'passage'; at: number; bm: BibleBookmark };

// Legacy rows predate anchor-size validation. Avoid argument-spreading their
// verse arrays into Math.min: a malformed large array can exceed the engine's
// call-argument limit before the row has a chance to render read-only.
function firstAnchorVerse(verses: number[]): number {
  let first = Infinity;
  for (const verse of verses) {
    if (Number.isInteger(verse) && verse > 0 && verse < first) first = verse;
  }
  return first;
}

export default function JournalPassagePage() {
  const { t } = useTranslation();
  const readOnly = isCoreReadOnly();
  const { bookSlug = '', chapter = '' } = useParams();

  const removeBookmark = useBookmarkStore((s) => s.removeBookmark);
  const { sources, state: sourceState, ready, retry: retrySources } =
    useJournalSources();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const [state, setState] = useState<LoadState>({ kind: 'pending' });
  const [retryTick, setRetryTick] = useState(0);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  const [chapterPage, setChapterPage] = useState(0);
  const [flowPage, setFlowPage] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState(false);

  const book = useMemo(
    () => CANONICAL_BOOKS.find((b) => b.slug === bookSlug),
    [bookSlug],
  );
  const inputsValid = !!book && CHAPTER_PATTERN.test(chapter);
  const chapterNum = inputsValid ? parseInt(chapter, 10) : 0;

  const marks = useMemo(
    () =>
      inputsValid
        ? chapterMarks(sources, book!.slug, chapterNum)
        : { chapterNotes: [], chapterPrayers: [], verses: [], passageNotes: [] },
    [sources, inputsValid, book, chapterNum],
  );

  // The "On this chapter" bucket (legacy whole-chapter notes + whole-chapter
  // prayers), combined recency-sorted; the page pages it (codex round-5 LOW).
  const chapterItems = useMemo<ChapterItem[]>(() => {
    const items: ChapterItem[] = [
      ...marks.chapterNotes.map((bm) => ({
        kind: 'note' as const,
        when: new Date(bm.updatedAt).getTime(),
        bm,
      })),
      ...marks.chapterPrayers.map((prayer) => ({
        kind: 'prayer' as const,
        when: new Date(prayer.updatedAt).getTime(),
        prayer,
      })),
    ];
    return items.sort((a, b) => b.when - a.when);
  }, [marks.chapterNotes, marks.chapterPrayers]);

  // Load the chapter's BSB blocks once; resolve each marked verse's text from
  // the Map. URL params (and the retry tick) drive the re-fetch, so the pending
  // setState is the documented set-state-in-effect deviation (rule #75).
  useEffect(() => {
    if (!inputsValid) return;
    let cancelled = false;
    setState({ kind: 'pending' });
    ensureBookAvailable(book!.slug)
      .then((bookData) => {
        if (cancelled) return;
        const blocks = bookData.chapters[String(chapterNum)] ?? null;
        if (!blocks) {
          // Hydrated but the chapter is missing: a real failure, not offline.
          setState({ kind: 'failed', offline: false });
          return;
        }
        setState({ kind: 'ready', verseTexts: chapterVerseTexts(blocks) });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ kind: 'failed', offline: isOfflineHydrationFailure(err) });
      });
    // Keep full-corpus offline hydration moving without blocking this book.
    ensureBibleHydrated().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [inputsValid, book, chapterNum, retryTick]);

  useEffect(() => {
    if (!book) return;
    const prior = document.title;
    document.title = `${book.name} ${chapterNum}`;
    return () => {
      document.title = prior;
    };
  }, [book, chapterNum]);

  if (!SCRIPTURE_JOURNAL_ENABLED)
    return <Navigate to="/bible" replace />;
  // Bad params are static (slug not in canon / non-positive chapter), safe to
  // redirect pre-hydration.
  if (!inputsValid) return <Navigate to="/bible/bookmarks" replace />;

  // Wait for the source loads to settle before treating empty marks as truly
  // empty (a direct load / refresh can render before IDB hydrates; codex
  // BLOCKER). The BSB verse-text load (state) is separate from source loading.
  if (!ready)
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
          <div
            ref={noticeReserveRef}
            className={styles.noticeReserve}
            style={{ height: noticeReserve }}
            data-notice-reserve={noticeReserve}
            aria-hidden="true"
          />
          <div className={styles.topBar}>
            <Link
              to={`/bible/bookmarks/book/${book!.slug}`}
              className={styles.backLink}
            >
              {t('bible.back')}
            </Link>
            <div className={styles.headerActions}>
              <BibleThemePicker />
            </div>
          </div>
          <h1 className={styles.title}>
            {book!.name} {chapterNum}
          </h1>
          <JournalSourceState state={sourceState} onRetry={retrySources} />
        </div>
      </div>
    );

  const isEmpty =
    marks.chapterNotes.length === 0 &&
    marks.chapterPrayers.length === 0 &&
    marks.verses.length === 0 &&
    marks.passageNotes.length === 0;
  // Fully un-marked (e.g. the chapter's last mark was just deleted from the
  // reflection sheet) -> back to the book page, which in turn redirects home if
  // the book has no marked chapters left (the redirect chain handles any depth).
  if (isEmpty)
    return <Navigate to={`/bible/bookmarks/book/${book!.slug}`} replace />;

  const verseTexts = state.kind === 'ready' ? state.verseTexts : null;
  // "Read the full chapter" lands on the first MARKED verse — a passage note's
  // first covered verse counts (a chapter can carry only passage notes).
  const firstMarkedVerse = marks.verses[0]?.verse ?? Infinity;
  const firstPassageVerse =
    marks.passageNotes.length > 0
      ? firstAnchorVerse(marks.passageNotes[0].anchor.verses)
      : Infinity;
  const firstVerse = Math.min(firstMarkedVerse, firstPassageVerse);
  const readFullTo = Number.isFinite(firstVerse)
    ? `/bible/${book!.slug}/${chapterNum}?v=${firstVerse}`
    : `/bible/${book!.slug}/${chapterNum}`;

  // Chunk-1 dedup (UX arc): interleave passage-note cards into the verse flow
  // at their FIRST covered verse, after that verse's own mark when both exist.
  // marks.passageNotes is already sorted (first verse asc, newest-first within
  // a verse) and Array.prototype.sort is stable, so ties keep that order.
  const flowItems: FlowItem[] = [
    ...marks.verses.map((vm) => ({ kind: 'verse' as const, at: vm.verse, vm })),
    ...marks.passageNotes.map((bm) => ({
      kind: 'passage' as const,
      at: firstAnchorVerse(bm.anchor.verses),
      bm,
    })),
  ].sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    if (a.kind !== b.kind) return a.kind === 'verse' ? -1 : 1;
    return 0;
  });

  const chapterPageCount = Math.max(
    1,
    Math.ceil(chapterItems.length / JOURNAL_PAGE_SIZE),
  );
  const boundedChapterPage = Math.min(chapterPage, chapterPageCount - 1);
  const chapterStart = boundedChapterPage * JOURNAL_PAGE_SIZE;
  const shownItems = chapterItems.slice(
    chapterStart,
    chapterStart + JOURNAL_PAGE_SIZE,
  );
  const flowPageCount = Math.max(
    1,
    Math.ceil(flowItems.length / JOURNAL_PAGE_SIZE),
  );
  const boundedFlowPage = Math.min(flowPage, flowPageCount - 1);
  const flowStart = boundedFlowPage * JOURNAL_PAGE_SIZE;
  const shownFlowItems = flowItems.slice(
    flowStart,
    flowStart + JOURNAL_PAGE_SIZE,
  );

  const renderChapterNote = (bm: BibleBookmark) => {
    const timeAgo = formatRelativeTime(new Date(bm.createdAt).getTime(), t);
    const confirming = confirmingDeleteId === bm.id;
    return (
      <li key={`note-${bm.id}`} className={styles.chapterItem}>
        <div className={styles.chapterTop}>
          <span className={styles.chapterLabel}>
            {t('bookmark.wholeChapterLabel')}
          </span>
          <span className={styles.itemTime}>{timeAgo}</span>
        </div>
        {bm.note ? (
          <p className={styles.itemNote}>{bm.note}</p>
        ) : (
          <p className={styles.itemEmptyNote}>
            {t('bookmark.emptyNotePlaceholder')}
          </p>
        )}
        <div className={styles.chapterActions}>
          {!readOnly && confirming ? (
            <>
              <span className={styles.deleteConfirmText}>
                {t('bookmark.deleteConfirm')}
              </span>
              <button
                type="button"
                className={styles.action}
                disabled={deletingId === bm.id}
                onClick={() => setConfirmingDeleteId(null)}
              >
                {t('bookmark.cancel')}
              </button>
              <button
                type="button"
                className={styles.delete}
                disabled={deletingId === bm.id}
                onClick={async () => {
                  if (readOnly) return;
                  setDeletingId(bm.id);
                  setDeleteError(false);
                  try {
                    await removeBookmark(bm.id);
                    setConfirmingDeleteId(null);
                  } catch (err) {
                    console.error('[Adia] bookmark delete failed', err);
                    setDeleteError(true);
                  } finally {
                    setDeletingId(null);
                  }
                }}
              >
                {t(
                  deletingId === bm.id
                    ? 'bookmark.deleting'
                    : 'bookmark.deleteConfirmAction',
                )}
              </button>
            </>
          ) : (
            <>
              <Link
                to={`/bible/${book!.slug}/${chapterNum}`}
                className={styles.readLink}
              >
                {t('bookmark.readChapter')}
              </Link>
              {!readOnly && (
                <button
                  type="button"
                  className={styles.delete}
                  onClick={() => setConfirmingDeleteId(bm.id)}
                >
                  {t('bookmark.delete')}
                </button>
              )}
            </>
          )}
        </div>
      </li>
    );
  };

  const renderChapterPrayer = (prayer: Prayer) => {
    const timeAgo = formatRelativeTime(new Date(prayer.createdAt).getTime(), t);
    // A whole-chapter prayer is not anchored to any single verse, so it links to
    // the chapter with no ?v= (mirrors the existing journal prayer row), not
    // readFullTo (which jumps to the first MARKED verse).
    return (
      <li key={`prayer-${prayer.id}`} className={styles.chapterItem}>
        <Link
          to={`/bible/${book!.slug}/${chapterNum}`}
          className={styles.chapterPrayerLink}
        >
          <div className={styles.chapterTop}>
            <span className={styles.prayerLabel}>
              {t('bookmark.prayerLabel')}
            </span>
            <span className={styles.itemTime}>{timeAgo}</span>
          </div>
          {prayer.rawText ? (
            <p className={styles.itemNote}>{prayer.rawText}</p>
          ) : null}
        </Link>
      </li>
    );
  };

  const renderVerseMark = (vm: VerseMark) => {
    const frozenAnchor =
      vm.notes[0]?.anchor ??
      vm.prayers.find((prayer) => {
        const prayerAnchor = prayer.scriptureAnchor;
        return (
          prayerAnchor?.verses.length === 1 &&
          prayerAnchor.verses[0] === vm.verse
        );
      })?.scriptureAnchor;
    const frozenVerseText =
      frozenAnchor?.verses.length === 1
        ? frozenAnchor.text.replace(new RegExp(`^${vm.verse}\\s+`), '')
        : undefined;
    const verseText =
      (verseTexts ? verseTexts.get(vm.verse) : undefined) ?? frozenVerseText;
    const fam = vm.highlight
      ? coerceFamily(vm.highlight.family) ?? 'plain'
      : null;
    // A highlighted verse wears its family color: a rail + colored number,
    // and the verse text gets a faint family wash (the "highlighter").
    // Non-highlight (note/prayer-only) verses stay neutral gold.
    const rail = fam ? HL_DOT_HEX[fam] : undefined;
    // Per-theme highlighter wash: the reader's already-tuned token for this
    // family (rule #81), so the wash reads on Paper/Light, not just dark.
    const wash = fam ? `var(--reader-hl-${fam})` : undefined;
    const notePreviews = vm.notes.slice(0, PASSAGE_VERSE_NOTE_PREVIEW);
    const newestPrayer = vm.prayers[0];
    const reflectionTo = `/bible/bookmarks/${book!.slug}/${chapterNum}/${vm.verse}`;
    return (
      <li
        key={vm.verse}
        className={`${styles.verseItem}${fam ? ' ' + styles.verseItemMarked : ''}`}
        style={rail ? { borderLeftColor: rail } : undefined}
      >
        <div className={styles.verseHead}>
          <span
            className={styles.verseNum}
            style={rail ? { color: rail } : undefined}
          >
            {vm.verse}
          </span>
          {fam && (
            <span className={styles.chip}>
              <span
                className={styles.chipDot}
                style={{ backgroundColor: HL_DOT_HEX[fam] }}
                aria-hidden="true"
              />
              {t(`bible.hlFamily.${fam}`)}
            </span>
          )}
        </div>
        {verseText ? (
          <p
            className={`${styles.verseText}${wash ? ' ' + styles.verseTextWashed : ''}`}
            style={wash ? { backgroundColor: wash } : undefined}
          >
            {verseText}
          </p>
        ) : null}

        {notePreviews.map((note) => (
          <Link key={note.id} to={reflectionTo} className={styles.notePreview}>
            {note.note ? (
              note.note
            ) : (
              <span className={styles.itemEmptyNote}>
                {t('bookmark.emptyNotePlaceholder')}
              </span>
            )}
          </Link>
        ))}
        {vm.notes.length > PASSAGE_VERSE_NOTE_PREVIEW && (
          <Link to={reflectionTo} className={styles.showAllNotes}>
            {t('bookmark.showAllNotes', { count: vm.notes.length })}
          </Link>
        )}

        {newestPrayer && (
          <div className={styles.prayedLine}>
            <span className={styles.prayedHead}>
              <span className={styles.prayedGlyph} aria-hidden="true">
                ✦
              </span>
              {t('bookmark.prayedHere')}
              {vm.prayers.length > 1 &&
                ' ' +
                  t('bookmark.prayedHereCount', {
                    count: vm.prayers.length,
                  })}
            </span>
            {newestPrayer.rawText ? (
              <p className={styles.prayedSnippet}>{newestPrayer.rawText}</p>
            ) : null}
          </div>
        )}
      </li>
    );
  };

  // Chunk-1 dedup (UX arc): a note covering 2+ verses is ONE stored row and
  // renders ONCE as a passage card — range label in the verse-number slot,
  // the anchor's FROZEN passage text (renders even offline / pre-hydration),
  // and the note body linking to the reflection thread at the first covered
  // verse (notesCoveringVerse finds the note from any covered verse).
  const renderPassageNote = (bm: BibleBookmark) => {
    const firstCovered = firstAnchorVerse(bm.anchor.verses);
    const reflectionTo = `/bible/bookmarks/${book!.slug}/${chapterNum}/${firstCovered}`;
    return (
      <li key={`passage-${bm.id}`} className={styles.verseItem}>
        <div className={styles.verseHead}>
          <span className={styles.verseNum}>
            {formatVerseRanges(bm.anchor.verses)}
          </span>
        </div>
        {bm.anchor.text ? (
          <p className={styles.verseText}>{bm.anchor.text}</p>
        ) : null}
        <Link to={reflectionTo} className={styles.notePreview}>
          {bm.note ? (
            bm.note
          ) : (
            <span className={styles.itemEmptyNote}>
              {t('bookmark.emptyNotePlaceholder')}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link
          to={`/bible/bookmarks/book/${book!.slug}`}
          className={styles.backLink}
        >
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>
      <h1 className={styles.title}>
        {book!.name} {chapterNum}
      </h1>
      <Link to={readFullTo} className={styles.readFull}>
        {t('bookmark.readFullChapter')}
      </Link>

      {state.kind === 'pending' && (
        <p className={styles.status}>{t('bible.chapterLoading')}</p>
      )}
      {state.kind === 'failed' && (
        <button
          type="button"
          className={styles.retryNotice}
          onClick={() => setRetryTick((n) => n + 1)}
        >
          {t(
            state.offline
              ? 'bible.offlineNeedsDownload'
              : 'bible.chapterReaderLoadFailed',
          )}
        </button>
      )}

      {chapterItems.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            {t('bookmark.passageWholeChapterHeading')}
          </h2>
          <ul className={styles.chapterList}>
            {shownItems.map((item) =>
              item.kind === 'note'
                ? renderChapterNote(item.bm)
                : renderChapterPrayer(item.prayer),
            )}
          </ul>
          {deleteError && (
            <p className={styles.mutationError} role="alert">
              {t('bookmark.deleteFailed')}
            </p>
          )}
          <JournalPagination
            page={boundedChapterPage}
            totalItems={chapterItems.length}
            onPageChange={setChapterPage}
          />
        </section>
      )}

      <ul className={styles.verseList}>
        {shownFlowItems.map((item) =>
          item.kind === 'verse'
            ? renderVerseMark(item.vm)
            : renderPassageNote(item.bm),
        )}
      </ul>
      <JournalPagination
        page={boundedFlowPage}
        totalItems={flowItems.length}
        onPageChange={setFlowPage}
      />
      </div>
    </div>
  );
}
